import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Link, Pencil } from 'lucide-react';
import {
    getPoliciesFromLinkedImplant,
    getCompletedPolicies,
    linkImplant,
    unlinkImplant,
} from '../../../services/api/policyService';
import AuthContext from '../../../store/auth-context';
import PERMISSION_KEYS from '../../../constants/permission-keys';
import RIGHTS_KEYS from '../../../constants/rights-keys';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import LoadingRing from '../../ui-components/LoadingRing';

interface PolicyLinkProps {
    implantId: string;
}

const PolicyLink = ({ implantId }: PolicyLinkProps) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const { toast } = useToast();
    const canManage =
        ctx.orRights([RIGHTS_KEYS.manageEmployees]) &&
        ctx.hasPermission(PERMISSION_KEYS.managePolicies);
    const canView =
        ctx.orRights([RIGHTS_KEYS.viewPolicies]) ||
        ctx.hasPermission(PERMISSION_KEYS.managePolicies);

    const [linkedPolicies, setLinkedPolicies] = useState<any[]>([]);
    const [allPolicies, setAllPolicies] = useState<any[]>([]);
    const [loadingAllPolicies, setLoadingAllPolicies] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingPolicyId, setPendingPolicyId] = useState<string | null>(null);

    const fetchLinkedPolicies = async () => {
        try {
            const data = await getPoliciesFromLinkedImplant(implantId);
            setLinkedPolicies(data);
        } catch (err) {
            console.error('Failed to fetch linked policies:', err);
        }
    };

    useEffect(() => {
        if (canView) {
            fetchLinkedPolicies();
        }
    }, [implantId]);

    useEffect(() => {
        if (isEditing) {
            setLoadingAllPolicies(true);
            getCompletedPolicies({ limit: 200 })
                .then((res) => setAllPolicies(res.data))
                .catch((err) => {
                    console.error('Failed to load policies:', err);
                    toast({
                        title: t('departmentPolicies.policyLink.failedToLoad'),
                        variant: 'destructive',
                    });
                })
                .finally(() => setLoadingAllPolicies(false));
        }
    }, [isEditing]);

    const linkedIds = new Set(linkedPolicies.map((p) => p._id));

    const handleToggle = async (policyId: string, checked: boolean) => {
        setPendingPolicyId(policyId);
        try {
            if (checked) {
                await linkImplant(policyId, implantId);
                const added = allPolicies.find((p) => p._id === policyId);
                if (added) {
                    setLinkedPolicies((prev) => [...prev, { ...added, document_id: { title: added.title } }]);
                }
            } else {
                await unlinkImplant(policyId, implantId);
                setLinkedPolicies((prev) => prev.filter((p) => p._id !== policyId));
            }
        } catch (err) {
            console.error('Failed to toggle policy link:', err);
            toast({
                title: checked
                    ? t('departmentPolicies.policyLink.failedToLink')
                    : t('departmentPolicies.policyLink.failedToUnlink'),
                variant: 'destructive',
            });
        } finally {
            setPendingPolicyId(null);
        }
    };

    if (!canView) return null;

    return (
        <>
            <Dialog open={isEditing} onOpenChange={setIsEditing}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{t('departmentPolicies.policyLink.dialogTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('departmentPolicies.policyLink.dialogDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-80 overflow-y-auto space-y-1">
                        {loadingAllPolicies ? (
                            <div className="flex justify-center py-8">
                                <LoadingRing size="small" />
                            </div>
                        ) : allPolicies.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4">
                                {t('departmentPolicies.policyLink.noCompleted')}
                            </p>
                        ) : (
                            allPolicies.map((policy) => {
                                const isLinked = linkedIds.has(policy._id);
                                return (
                                    <label
                                        key={policy._id}
                                        className="flex items-center gap-3 rounded-lg border border-subtle bg-card px-4 py-3 cursor-pointer hover:bg-muted/40"
                                    >
                                        <Checkbox
                                            checked={isLinked}
                                            disabled={pendingPolicyId === policy._id}
                                            onCheckedChange={(checked) =>
                                                handleToggle(policy._id, !!checked)
                                            }
                                        />
                                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">
                                                {policy.title || t('departmentPolicies.policyLink.untitled')}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(policy.updatedAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        {pendingPolicyId === policy._id && (
                                            <span className="text-xs text-muted-foreground">
                                                {t('departmentPolicies.policyLink.saving')}
                                            </span>
                                        )}
                                    </label>
                                );
                            })
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditing(false)}>
                            {t('departmentPolicies.policyLink.done')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {canManage && linkedPolicies.length === 0 && (
                <Button
                    variant="outline"
                    className="w-full bg-[#3d6b2e] border-[#3d6b2e] text-white hover:bg-[#4a7d38] hover:text-white"
                    onClick={() => setIsEditing(true)}
                >
                    <Link className="h-4 w-4 mr-2" />
                    {t('departmentPolicies.policyLink.linkPolicy')}
                </Button>
            )}
            {linkedPolicies.length > 0 && (
                <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                        {t('departmentPolicies.policyLink.linkedActivePolicies')}
                        {canManage && (
                            <span
                                className="cursor-pointer text-muted-foreground hover:text-foreground"
                                onClick={() => setIsEditing(true)}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </span>
                        )}
                    </h4>
                    {linkedPolicies.map((policy) => (
                        <a
                            key={policy._id}
                            href={`/department-policies/${policy._id}`}
                            className="flex items-center gap-2 text-sm text-[#8bd36a] hover:text-[#a0e080]"
                        >
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            {policy.document_id?.title || policy.title || t('departmentPolicies.policyLink.untitled')}
                        </a>
                    ))}
                </div>
            )}
        </>
    );
};

export default PolicyLink;
